import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Car, ArrowLeft, Gauge, Ban, IdCard, MapPinned, HelpCircle, Building2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RatingWidget } from "@/components/RatingWidget";
import { SeoFooter } from "@/components/SeoFooter";
import { CITIES, CITY_PATH } from "@/lib/cities";

function useSeo(title, description, path) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && description) meta.setAttribute("content", description);

    // Keep canonical + Open Graph aligned with the current page.
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const prevOgTitle = ogTitle ? ogTitle.getAttribute("content") : null;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOgUrl = ogUrl ? ogUrl.getAttribute("content") : null;
    const url = window.location.origin + (path || window.location.pathname);
    if (canonical) canonical.setAttribute("href", url);
    if (ogTitle) ogTitle.setAttribute("content", title);
    if (ogUrl) ogUrl.setAttribute("content", url);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
      if (ogTitle && prevOgTitle) ogTitle.setAttribute("content", prevOgTitle);
      if (ogUrl && prevOgUrl) ogUrl.setAttribute("content", prevOgUrl);
    };
  }, [title, description, path]);
}

const Section = ({ icon: Icon, title, children }) => (
  <section className="mt-8">
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <h2 className="font-display text-xl font-bold text-foreground">{title}</h2>
    </div>
    <div className="space-y-3 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export default function About() {
  useSeo(
    "À propos — vspo, le GPS gratuit et libre pour voiture sans permis",
    "vspo est un GPS gratuit, libre d'accès et sans inscription pour voiture sans permis (VSP). Tout savoir : vitesse 45 km/h, permis AM, routes interdites (autoroutes, voies rapides) et itinéraires adaptés en France.",
    "/a-propos"
  );

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS gratuit voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
              data-testid="about-back-to-app"
            >
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-6">
        <h1 className="font-display text-3xl font-bold text-foreground">
          La voiture sans permis (VSP) : réglementation et itinéraires
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Une <strong>voiture sans permis</strong> (VSP), aussi appelée <em>voiturette</em> ou
          quadricycle léger à moteur, est un véhicule conçu pour se déplacer facilement sans
          permis B. <strong>vspo</strong> est un <strong>GPS gratuit et libre d'accès, sans
          inscription</strong>, qui intègre ces règles pour vous proposer des itinéraires réellement
          adaptés en France.
        </p>

        <Section icon={Gauge} title="Vitesse et caractéristiques">
          <p>
            La VSP est bridée par construction à <strong>45 km/h</strong>. Sa cylindrée n'excède pas
            50 cm³ (essence) ou une puissance de 6 kW (diesel/électrique), et elle est limitée à
            deux places. Ces caractéristiques la classent comme quadricycle léger à moteur.
          </p>
        </Section>

        <Section icon={IdCard} title="Quel permis pour conduire une VSP ?">
          <p>
            La conduite d'une VSP nécessite le <strong>permis AM</strong> (ancien BSR),
            accessible dès <strong>14 ans</strong> après une formation de 8 heures en auto-école.
            Les personnes nées avant le 1ᵉʳ janvier 1988 en sont dispensées. L'assurance du
            véhicule et le port de la ceinture sont obligatoires.
          </p>
        </Section>

        <Section icon={Ban} title="Où une VSP peut-elle rouler ?">
          <p>
            La VSP est <strong>strictement interdite sur les autoroutes</strong> ainsi que sur les
            <strong> voies rapides et voies pour automobiles</strong> (généralement signalées par un
            panneau à fond bleu). Elle circule sur les routes ordinaires, en ville comme à la
            campagne, dans le respect de la signalisation locale et des routes réglementées.
          </p>
          <p>
            C'est précisément là que <strong>vspo</strong> vous aide : chaque itinéraire évite
            automatiquement les autoroutes et les voies rapides connues, et signale en rouge les
            tronçons à surveiller.
          </p>
        </Section>

        <Section icon={MapPinned} title="Comment vspo calcule vos trajets">
          <p>
            vspo s'appuie sur OpenStreetMap et OpenRouteService pour tracer des itinéraires
            <strong> pensés pour 45 km/h</strong>, proposer jusqu'à 3 alternatives, une navigation
            vocale, la recherche d'adresses, vos favoris et les lieux d'intérêt à proximité
            (supermarchés, restaurants, stations…). Vous pouvez même signaler vous-même une voie
            rapide interdite pour l'exclure de vos futurs trajets.
          </p>
        </Section>

        <Section icon={HelpCircle} title="Questions fréquentes (FAQ)">
          <div>
            <h3 className="text-base font-semibold text-foreground">Une voiture sans permis peut-elle rouler sur l'autoroute ?</h3>
            <p className="mt-1">
              Non. Les VSP sont strictement interdites sur les autoroutes, les voies rapides et les
              voies pour automobiles. vspo évite automatiquement ces axes lors du calcul d'itinéraire.
            </p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Quel permis faut-il pour conduire une VSP ?</h3>
            <p className="mt-1">
              Le permis AM (ancien BSR), accessible dès 14 ans après une formation de 8 heures. Les
              personnes nées avant le 1ᵉʳ janvier 1988 en sont dispensées.
            </p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">vspo est-il gratuit ?</h3>
            <p className="mt-1">
              Oui, vspo est un GPS 100 % gratuit pour voitures sans permis : itinéraires 45 km/h sans
              autoroute, navigation vocale, trafic en temps réel, favoris et lieux d'intérêt en France.
            </p>
          </div>
        </Section>

        <Section icon={Building2} title="GPS voiture sans permis par ville">
          <p>
            Retrouvez des conseils d'itinéraires pour voiture sans permis dans les principales villes
            de France :
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`${CITY_PATH}/${c.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                data-testid={`about-city-link-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </Section>

        <div className="mt-10">
          <RatingWidget />
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-4">
          <h2 className="font-display text-base font-bold text-foreground">Nos conseils pour rouler en VSP</h2>
          <p className="mt-1 text-sm text-muted-foreground">Assurance, entretien, recharge électrique et plus encore.</p>
          <Link
            to="/conseils"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            data-testid="about-blog-link"
          >
            Lire le blog conseils VSP →
          </Link>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Prêt à planifier votre trajet ?</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Lancez le GPS vspo et obtenez un itinéraire adapté à votre voiture sans permis.
          </p>
          <Link
            to="/"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <Car className="h-4 w-4" /> Ouvrir vspo
          </Link>
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Informations fournies à titre indicatif. Vous restez responsable du respect du code de la
          route et de la signalisation.
        </p>
      </main>
      <SeoFooter />
    </div>
  );
}
